import { css } from '@emotion/react';
import styled from '@emotion/styled';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CategoryTypeString } from '@diritto/common/content/constants';
import { ContentPublicRelayDto } from '@diritto/common/relay/dtos/content-relay.dtos';
import { ProfileLitePublicRelayDto } from '@diritto/common/relay/dtos/profile-lite-relay.dtos';
import { Color } from '@diritto/common/style/color';
import { FontWeight, Typography, TypographySize } from '@diritto/common/style/typography';
import { ContentDetailIconCounts } from 'app-components/contents/detail/ContentDetailIconCounts';
import { ContentDetailInfoMetaKeywords } from 'app-components/contents/detail/ContentDetailInfoMetaKeywords';
import { ProfileName } from 'app-components/contents/ProfileName';
import { isPC } from 'constants/layout';
import { LockIcon } from 'icons/LockIcon';
import { getTextLineStyle } from 'styles/common';
import { DotDivider } from 'ui-components/common/DotDivider';

interface DetailInfoMetaDataProps {
  content: ContentPublicRelayDto;
  profile?: ProfileLitePublicRelayDto;
  isPrivate?: boolean;
}

interface DetailInfoMetaDescriptionProps {
  description: string;
  keywords: string[];
}

const MAX_DESCRIPTION_LINE_HEIGHT = TypographySize.XXSmall.lineHeight * 3;

export const DetailInfoMetaData = ({ content, profile, isPrivate = false }: DetailInfoMetaDataProps) => (
  <>
    <StyledTitleWrapper>
      {isPrivate && <LockIcon className="size-[20px]" color={Color.Gray500} />}
      <StyledTitle>{content.title}</StyledTitle>
    </StyledTitleWrapper>
    <StyledMeta>
      <ProfileName profile={profile} />
      <DotDivider />
      <div>{CategoryTypeString[content.categoryType]}</div>
      {content.counts.episodeCount > 0 && (
        <>
          <DotDivider />
          <div>{`${content.counts.episodeCount}화`}</div>
        </>
      )}
    </StyledMeta>
    <StyledContentDetailIcons content={content} />
  </>
);

export const DetailInfoMetaDescription = ({ description, keywords }: DetailInfoMetaDescriptionProps) => {
  const ref = useRef<HTMLParagraphElement>(null);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    if (ref?.current) {
      // 애초에 3줄이 넘지 않으면 전체 보여줌
      setShowAll(ref.current.clientHeight <= MAX_DESCRIPTION_LINE_HEIGHT);
    }
  }, []);

  const handleShowAll = useCallback(() => {
    if (!showAll) {
      setShowAll(true);
    }
  }, [showAll]);

  return (
    <>
      <StyledDescriptionWrapper showAll={showAll} onClick={handleShowAll}>
        <StyledDescription showAll={showAll}>{description}</StyledDescription>
        <StyledHideDescription showAll ref={ref}>
          {description}
        </StyledHideDescription>
        {!showAll && <StyledShowMore>...더보기</StyledShowMore>}
      </StyledDescriptionWrapper>
      <StyledContentDetailInfoMetaKeywords keywords={keywords} />
    </>
  );
};

const StyledTitleWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
`;

const StyledTitle = styled.p`
  font-weight: ${FontWeight.Bold};
  ${Typography.Medium};
  ${getTextLineStyle(3)};

  @media (${isPC}) {
    ${Typography.Large};
  }
`;

const StyledContentDetailIcons = styled(ContentDetailIconCounts)`
  margin-top: 10px;
  margin-bottom: 5px;
`;

const StyledDescriptionWrapper = styled.section(
  ({ showAll }: { showAll: boolean }) => css`
    margin-top: 16px;
    position: relative;
    height: ${showAll ? 'inherit' : `${MAX_DESCRIPTION_LINE_HEIGHT}px`};
    overflow: hidden;
    word-break: break-all;
  `,
);

const StyledDescription = styled.p(
  ({ showAll }: { showAll: boolean }) => css`
    color: ${Color.Gray700};
    ${Typography.XXSmall};
    font-weight: ${FontWeight.Medium};
    white-space: ${showAll ? 'break-spaces' : 'inherit'};
  `,
);

const StyledHideDescription = styled(StyledDescription)`
  position: absolute;
  width: 100%;
  top: 0;
  opacity: 0;
  pointer-events: none;
`;

const StyledContentDetailInfoMetaKeywords = styled(ContentDetailInfoMetaKeywords)`
  margin-top: 16px;
`;

const StyledShowMore = styled.button`
  position: absolute;
  width: 90px;
  text-align: right;
  background: linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, #ffffff 50%);
  bottom: 0;
  right: 0;
  ${Typography.XXXSmall};
  color: ${Color.Gray500};
`;

const StyledMeta = styled.div`
  display: flex;
  flex-direction: row;
  gap: 4px;
  align-items: center;

  font-weight: ${FontWeight.Medium};
  ${Typography.XXSmall};
  color: ${Color.Gray600};
`;
